<template>
  <div class="modal-backdrop">
    <VueGlobalEvents @keyup.esc="onClose" />
    <VueFocusLock>
      <div class="modal-content">
        <button type="button" class="close" @click="onClose">
          <VSvgIcon name="cancel" variant="outlined" size="middle" />
          <span class="sr-only">{{ $t('common.close') }}</span>
        </button>
        <div>
          <div class="modal-header">
            <h2 class="h4 modal-title">{{ $t('autotopupregistrations.deletion_header_label', { autoTopUpId }) }}</h2>
          </div>
          <div class="modal-body">
            <VSpinner v-if="isLoading" />
            <VNotification v-else-if="isSuccess" type="SUCCESS" center>
              <span v-html="$to('autotopupregistrations.deletion_success_message')" />
            </VNotification>
            <VNotification v-else-if="isError" type="ERROR" center>
              <span v-html="errorText" />
            </VNotification>
            <VForm v-else :form="form" @submit="onSubmit">
              <VSelect
                v-focus
                name="contractPartner2InCall"
                label="autotopupregistrations.deletion_cp2_in_call_label"
                placeholder="autotopupregistrations.deletion_cp2_in_call_placeholder"
                hint="autotopupregistrations.deletion_cp2_in_call_required"
                required
              >
                <option value="yes">{{ $t('common.yes') }}</option>
                <option value="no">{{ $t('common.no') }}</option>
              </VSelect>
              <VSelect
                name="createdByCaller"
                label="autotopupregistrations.deletion_created_by_caller_label"
                placeholder="autotopupregistrations.deletion_created_by_caller_placeholder"
                hint="autotopupregistrations.deletion_created_by_caller_required"
                required
              >
                <option value="yes">{{ $t('common.yes') }}</option>
                <option value="no">{{ $t('common.no') }}</option>
              </VSelect>
              <div class="offset-top-2 text-center">
                <VButton type="submit" :disabled="isSubmitDisabled" class="btn-small btn-brand" label="autotopupregistrations.deletion_submit_button_label" />
                <VButton type="button" class="btn-small offset-left-1" label="common.abort" @click="onClose" />
              </div>
            </VForm>
          </div>
        </div>
      </div>
    </VueFocusLock>
  </div>
</template>

<script>
import VueFocusLock from 'vue-focus-lock';
import VueGlobalEvents from 'vue-global-events';
import focus from '@/directives/focus';
import VForm from '@/components/Form/Form';
import VSelect from '@/components/Form/Select';
import VButton from '@/components/Button';
import Proxy from '@/proxies/AutoTopUpRegistrationsProxy';
import VNotification from '@/components/Notification';
import VSpinner from '@/components/Spinner';
import { focusFirst, getFormValues, initFields } from '@/utils/form';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';

export default {
  name: 'DeletionModal',

  directives: {
    focus,
  },

  components: {
    VueGlobalEvents,
    VueFocusLock,
    VForm,
    VSelect,
    VButton,
    VNotification,
    VSpinner,
    VSvgIcon,
  },

  props: {
    autoTopUpId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      status: 'LOADING',
      form: null,
      errorType: '',
    };
  },

  computed: {
    isLoading() {
      return this.status === 'LOADING';
    },

    isSuccess() {
      return this.status === 'SUCCESS';
    },

    isError() {
      return this.status === 'ERROR';
    },

    errorText() {
      return this.$tf(`autotopupregistrations.deletion_error_${this.errorType}`, () => this.$t('autotopupregistrations.deletion_error_default'));
    },

    isSubmitDisabled() {
      const values = getFormValues(this.form);
      return values.contractPartner2InCall === 'no' && values.createdByCaller === 'no';
    },
  },

  created() {
    this.previousElement = document.activeElement;
    this.form = initFields(
      {
        name: 'contractPartner2InCall',
      },
      {
        name: 'createdByCaller',
      }
    );
    focusFirst(this.form);
    this.status = 'PROMPT';
  },

  methods: {
    onClose() {
      this.$emit('close');
      this.$nextTick(() => {
        this.previousElement.focus();
      });
    },

    async onSubmit({ values }) {
      this.status = 'LOADING';
      await new Proxy()
        .delete(this.autoTopUpId, {
          contractPartner2InCall: values.contractPartner2InCall === 'yes',
          createdByCaller: values.createdByCaller === 'yes',
        })
        .then(() => {
          this.$emit('loadData');
          this.status = 'SUCCESS';
        })
        .catch((err) => {
          this.errorType = err.data.code;
          this.status = 'ERROR';
        });
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
}

.modal-content {
  max-width: 820px;
  margin: auto;
}
</style>
